import { use, useMemo } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { sendCallNotification } from '@app/api'
import config from '@app/config'
import { ChatContext } from '@app/context'
import { getContextEntity } from '@app/helpers'
import {
  useConversationActorRole,
  useConversation,
  useUser,
  useCountries,
  useLanguages,
  useIsStaffWithoutStaffAdmin,
} from '@app/hooks'
import {
  trackCallAgentfromMessages,
  trackCallBuyerFromMessages,
  trackFollowUpCallContact,
  trackUnlockContactDetailsFromMessages,
  trackViewProfileFromMessages,
} from '@app/services/tracking'

import { getUnlockContactDetailsUrl, isB2BView } from '@app/utils'

import { Avatar, Icon } from '@lib/components'
import { IconProfile } from '@lib/icons'

import styles from './styles.module.scss'
import { ContactDetailsProps } from './types'


export const ContactDetails = ({ participant }: ContactDetailsProps) => {
  const { t } = useTranslation()
  const {
    avatar,
    fullName,
    jobTitle,
    phone,
    isB2CParticipant,
    url,
    email,
    obfuscate,
    shouldFallbackToAccount,
  } =
    participant
  const { countries } = useCountries()
  const { state, dispatch } = use(ChatContext)
  const userState = useUser()
  const isStaffWithoutStaffAdmin = useIsStaffWithoutStaffAdmin()
  const conversation = useConversation()
  const currentParticipant = useMemo(
    () =>
      conversation?.participants.find(
        (participantItem) =>
          participantItem.userId === userState.currentUser?.id
      ),
    [conversation?.participants, userState.currentUser?.id]
  )
  
  const listing = conversation
    ? getContextEntity(state.listings, conversation)
    : null
  const conversationActorRole = useConversationActorRole()

  const isB2Cuser =
    !!userState.currentUser.accountsRoles?.find(
      (role) => role.role === 'Visitor'
    ) && !isB2BView()
  const isB2Buser =
    !!userState.currentUser.accountsRoles?.find(
      (role) => role.role === 'Admin' || role.role === 'Agent'
    ) && isB2BView()

  const { languages } = useLanguages()

  const country = useMemo(
    () => countries?.find((c) => c.id === participant?.country)?.name || '',
    [countries, participant?.country]
  )

  const language = useMemo(
    () => languages?.find((l) => l.id === participant.language)?.name || '',
    [languages, participant.language]
  )

  const unlockUrl = useMemo(() => { 
    return getUnlockContactDetailsUrl(userState, config.regionalSettings)
  }, [userState, config.regionalSettings])

  if (!participant) {
    return null
  }

  const avatarTemplate = shouldFallbackToAccount ? 'logo-md' : 'user-sm'

  const handlePhoneClick = async () => {
    if (isB2Cuser) {
      trackCallAgentfromMessages(
        conversation?.id,
        listing,
        conversationActorRole
      )
    } else if (isB2Buser) {
      trackCallBuyerFromMessages(
        conversation?.id,
        listing,
        conversationActorRole
      )
    }

    if (!conversation) {
      return
    }

    const pendingSuggestedActionId = currentParticipant
      ? conversation.suggestedActions?.find(
        (action) =>
          action.status === 'Pending' &&
            action.toParticipantId === currentParticipant.id
      )?.id
      : undefined

    if (!pendingSuggestedActionId) {
      return
    }

    try {
      await sendCallNotification({
        conversationId: conversation.id,
        suggestedActionId: pendingSuggestedActionId,
        toParticipantId: participant.id,
      })

      trackFollowUpCallContact(conversation.id, listing, conversationActorRole)
      dispatch({
        type: 'SET_SUGGESTED_ACTION_STATUS',
        payload: {
          conversationId: conversation.id,
          suggestedActionId: pendingSuggestedActionId,
          status: 'Clicked',
        },
      })
    } catch (error) {
      console.error('Failed to send call notification', error)
    }
  }

  const handleDetailsClick = () => {
    if (isB2Buser) {
      trackUnlockContactDetailsFromMessages(
        conversation?.id,
        listing,
        conversationActorRole
      )
    }
  }

  const handleViewProfileClick = () => {
    if (isB2Cuser) {
      trackViewProfileFromMessages(
        conversation?.id,
        listing,
        conversationActorRole
      )
    }
  }

  const isCallDisabled = isStaffWithoutStaffAdmin && isB2CParticipant

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Avatar
          size="large"
          avatar={avatar}
          fullName={fullName}
          template={avatarTemplate}
        />
        <div className={styles.titles}>
          <span className={styles.name}>{fullName}</span>
          <span className={styles.position}>{jobTitle}</span>
        </div>
      </div>

      {(phone || url) && (
        <div className={styles.actions}>
          {phone && !obfuscate && (
            <a
              className={classNames(styles.button, {
                [styles.disabled]: isCallDisabled,
              })}
              href={isCallDisabled ? undefined : `tel:${phone}`}
              target="_blank"
              rel="noreferrer"
              aria-disabled={isCallDisabled}
              tabIndex={isCallDisabled ? -1 : undefined}
              onClick={isCallDisabled ? undefined : handlePhoneClick}
            >
              <Icon path="icons/icon-phone" />
              {t('ItemDetails.Agent.Action.Call')}
            </a>
          )}
          {url && (
            <a
              className={styles.button}
              href={url}
              target="_blank"
              rel="noreferrer"
              onClick={handleViewProfileClick}
            >
              <IconProfile />
              {t('Chat.Contact.View.Profile')}
            </a>
          )}
        </div>
      )}
      {obfuscate && unlockUrl && (
        <a
          className={classNames(styles.button, styles.primary)}
          href={unlockUrl}
          target="_blank"
          rel="noreferrer"
          onClick={handleDetailsClick}
        >
          {t('Chat.Contact.UnlockContactDetails.Button')}
        </a>
      )}
      <div className={styles.body}>
        <span className={styles.about}>
          {t('Chat.Contact.About')} {fullName}
        </span>

        <div className={styles.contacts}>
          {phone && (
            <div className={styles.section}>
              <span className={styles.label}>
                {t('Chat.Contact.Phone.Number')}
              </span>
              <span className={styles.value}>{phone}</span>
            </div>
          )}

          {email && (
            <div className={styles.section}>
              <span className={styles.label}>{t('Chat.Contact.Email')}</span>
              <span className={styles.value}>{email}</span>
            </div>
          )}

          {country && (
            <div className={styles.section}>
              <span className={styles.label}>{t('Chat.Contact.Country')}</span>
              <span className={styles.value}>{country}</span>
            </div>
          )}

          {language && (
            <div className={styles.section}>
              <span className={styles.label}>{t('Chat.Contact.Language')}</span>
              <span className={styles.value}>{language}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
